import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import Lang from '../../../../config/lang'
import { useProfileTab } from '../../ProfileTabContext'

const ProfileSidebar = ({
  profileData,
  jobAppliedTodal,
  profileCompletion,
  showAppliedJobsSummary = true,
  showEditProfileButton = true,
}) => {
  const { setCurrentTabIndex } = useProfileTab()
  const { lang, name, phone } = useSelector((state) => state.globalReducer)

  function getInitials(name) {
    if (!name) return ''
    const parts = name.trim().split(/\s+/)

    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase()
    }

    const first = parts[0][0]
    const last = parts[parts.length - 1][0]
    return (first + last).toUpperCase()
  }

  const formatPhoneNumber = (value) => {
    if (!value) return ''

    const cleanedValue = value.replace(/\D/g, '')
    const normalizedValue = cleanedValue.startsWith('0')
      ? `62${cleanedValue.substring(1)}`
      : cleanedValue.startsWith('62')
        ? cleanedValue
        : `62${cleanedValue}`

    const localNumber = normalizedValue.substring(2)
    const firstGroup = localNumber.slice(0, 3)
    const secondGroup = localNumber.slice(3, 7)
    const thirdGroup = localNumber.slice(7, 11)
    const remainingGroup = localNumber.slice(11)
    const groupedNumber = [firstGroup, secondGroup, thirdGroup, remainingGroup]
      .filter(Boolean)
      .join(' ')

    return `+62 ${groupedNumber}`
  }

  const profilePath = `/${lang || 'id'}/profile`
  const displayName = name || profileData?.fullName
  const displayPhone = formatPhoneNumber(
    phone || profileData?.mobilePhoneNumber,
  )

  return (
    <div className='flex w-full flex-col items-center rounded-[12px] border border-[#E5E7EB] bg-white p-5 shadow-[0px_1px_2px_rgba(0,0,0,0.05)] sm:p-[25px] lg:w-[280px] lg:min-w-[280px] lg:self-start lg:sticky lg:top-[135px] lg:mr-5'>
      <div className='relative h-24 w-24 overflow-hidden rounded-full'>
        <img
          src='/asset/images/profile_bg.png'
          alt='profile background'
          className='object-cover w-full h-full'
        />

        <div className='absolute inset-0 flex items-center justify-center'>
          <span className='mt-2 text-[20px] font-bold text-white'>
            {getInitials(displayName)}
          </span>
        </div>
      </div>

      <h1 className='mt-6 text-center text-[20px] font-bold leading-[28px] text-[#111827]'>
        {displayName}
      </h1>

      <div className='mt-6 flex w-full items-center justify-center gap-2'>
        <img
          src='/asset/icon/phone.svg'
          alt='phone'
          className='h-4 w-4 object-contain'
        />
        <p className='min-w-0 break-all text-center text-[14px] font-normal leading-[15.12px] text-[#48494B]'>
          {displayPhone}
        </p>
      </div>

      <div className='mt-2 flex w-full items-center justify-center gap-2'>
        <img
          src='/asset/icon/email.svg'
          alt='email'
          className='h-4 w-4 object-contain'
        />
        <p className='min-w-0 break-all text-center text-[14px] font-normal leading-[15.1px] text-[#48494B]'>
          {profileData?.email}
        </p>
      </div>

      <div className='my-6 h-px w-full bg-[#E5E7EB]' />

      {showAppliedJobsSummary && (
        <div className='flex w-full items-center justify-between rounded-[8px] border border-[#DBEAFE] bg-[#EFF6FF] p-[17px]'>
          <div>
            <p className='text-[14px] font-regular leading-[20px] text-[#1E3A8A]'>
              {Lang(lang, 'applied_jobs')}
            </p>
            <p className='text-[24px] font-semibold leading-[32px] text-[#1D4ED8]'>
              {jobAppliedTodal ?? 0}
            </p>
          </div>
          <div className='flex h-12 w-12 items-center justify-center rounded-xl bg-[#DBEAFE]'>
            <img
              src='/asset/icon/job_blue.svg'
              alt='applied jobs'
              className='h-5 w-5 object-contain'
            />
          </div>
        </div>
      )}

      <div
        className={`flex w-full items-center justify-between rounded-[8px] border border-[#F3E8FF] bg-[#FAF5FF] p-[17px] ${
          showAppliedJobsSummary ? 'mt-4' : ''
        }`}
      >
        <div>
          <p className='text-[14px] font-regular leading-[20px] text-[#581C87]'>
            {Lang(lang, 'profile_completion')}
          </p>
          <div className='flex flex-row items-center'>
            <p className='text-[24px] font-semibold leading-[32px] text-[#7E22CE]'>
              {`${profileCompletion ?? 0}/10`}
            </p>
            <p className='ml-1 text-[14px] font-regular leading-[20px] text-[#7E22CE]'>
              Forms
            </p>
          </div>
          <p className='text-[12px] font-regular leading-[16px] text-[#7E22CE]'>
            Est. 15 min
          </p>
        </div>
        <div className='flex h-12 w-12 items-center justify-center rounded-xl bg-[#F3E8FF]'>
          <img
            src='/asset/icon/profile_purple.svg'
            alt='profile completion'
            className='h-5 w-5 object-contain'
          />
        </div>
      </div>

      {showEditProfileButton && (
        <Link
          to={profilePath}
          onClick={() => {
            setCurrentTabIndex(0)
          }}
          className='mt-6 inline-flex items-center gap-2 text-[14px] font-medium leading-[20px] text-[#2563EB]'
        >
          <span
            aria-hidden='true'
            className='h-4 w-4 bg-[#2563EB]'
            style={{
              WebkitMaskImage: "url('/asset/icon/edit.svg')",
              maskImage: "url('/asset/icon/edit.svg')",
              WebkitMaskRepeat: 'no-repeat',
              maskRepeat: 'no-repeat',
              WebkitMaskPosition: 'center',
              maskPosition: 'center',
              WebkitMaskSize: 'contain',
              maskSize: 'contain',
            }}
          />
          <span>Edit Profile</span>
        </Link>
      )}
    </div>
  )
}
export default ProfileSidebar
